"use client";
import { C as DialogClose$1, E as DialogRoot$1, S as DialogDescription$1, T as AlertDialogRoot, b as DialogPortal, v as DialogTrigger$1, w as DialogBackdrop, x as DialogPopup, y as DialogTitle$1 } from "../chunks/vendor-base-ui-n0ony04mhklkmnfd.js";
import { t as cn } from "../chunks/cn-dk9nmc4mhy1765ud.js";
import { Button } from "./button.js";
import { n as usePortalContainer } from "../chunks/portal-provider-mv0x5fh2psxbmcl8.js";
import { Box, Row } from "./box.js";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import IconX from "@tabler/icons-react/dist/esm/icons/IconX.mjs";
//#region src/components/dialog/dialog.tsx
/** Dialog size variant definitions mapping sizes to their minimum widths. */
var ATOMS_DIALOG_VARIANTS = {
	size: {
		base: {
			classes: "sm:min-w-96",
			description: "Default dialog width"
		},
		sm: {
			classes: "sm:min-w-72",
			description: "Small dialog for simple confirmations"
		},
		lg: {
			classes: "sm:min-w-[32rem]",
			description: "Large dialog for complex content"
		},
		xl: {
			classes: "sm:min-w-[48rem]",
			description: "Extra large dialog for detailed views"
		}
	},
	role: {
		dialog: {
			classes: "",
			description: "Standard dialog for general-purpose modals"
		},
		alertdialog: {
			classes: "",
			description: "Alert dialog for confirmation flows requiring explicit user acknowledgment"
		}
	}
};
var ATOMS_DIALOG_DEFAULT_VARIANTS = {
	size: "base",
	role: "dialog"
};
var ATOMS_DIALOG_STYLING = {
	dimensions: {
		sm: {
			width: 288,
			titleSize: 45,
			descSize: 16,
			padding: 48,
			gap: 24,
			buttonSize: "sm"
		},
		base: {
			width: 384,
			titleSize: 45,
			descSize: 16,
			padding: 48,
			gap: 24,
			buttonSize: "base"
		},
		lg: {
			width: 512,
			titleSize: 45,
			descSize: 16,
			padding: 48,
			gap: 24,
			buttonSize: "base"
		},
		xl: {
			width: 768,
			titleSize: 45,
			descSize: 16,
			padding: 48,
			gap: 24,
			buttonSize: "base"
		}
	},
	baseTokens: {
		background: "color-surface",
		text: "text-color-surface",
		borderRadius: 32,
		shadow: "shadow-lg"
	},
	backdrop: {
		background: "color-od-bg-scrim",
		opacity: .72
	},
	header: {
		title: {
			fontWeight: 500,
			color: "text-color-surface"
		},
		closeIcon: {
			name: "tabler-x",
			size: 24,
			color: "text-color-surface"
		}
	},
	description: {
		fontWeight: 400,
		color: "text-color-surface"
	},
	buttons: {
		primary: {
			background: "color-primary",
			text: "white"
		},
		secondary: {
			ring: "color-border",
			text: "text-color-surface"
		}
	}
};
var DialogRoleContext = createContext("dialog");
function useDialogRole() {
	return useContext(DialogRoleContext);
}
function dialogVariants({ size = ATOMS_DIALOG_DEFAULT_VARIANTS.size } = {}) {
	return cn("fixed bg-od-bg-primary text-od-content-primary shadow-lg duration-150 transition-[translate,scale,opacity]", "inset-x-0 bottom-0 w-full max-w-none translate-y-0 rounded-t-[2rem] max-sm:data-starting-style:translate-y-full max-sm:data-ending-style:translate-y-full", "sm:inset-x-auto sm:top-1/2 sm:bottom-auto sm:left-1/2 sm:w-auto sm:max-w-[calc(100vw-3rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:overflow-hidden sm:rounded-[2rem] sm:data-starting-style:scale-90 sm:data-starting-style:opacity-0 sm:data-ending-style:scale-90 sm:data-ending-style:opacity-0", ATOMS_DIALOG_VARIANTS.size[size].classes);
}
/** Distance in px the sheet must be dragged down before it dismisses on release. */
var SHEET_DRAG_DISMISS_THRESHOLD = 96;
/**
* Drag-to-dismiss for the mobile bottom sheet. Tracks a downward drag on the
* sheet handle, following the finger while dragging, and clicks the hidden
* `Dialog.Close` once the drag passes the threshold; otherwise it snaps back.
*/
function useSheetDragDismiss() {
	const closeRef = useRef(null);
	const startYRef = useRef(0);
	const cleanupRef = useRef(() => {});
	const [dragOffset, setDragOffset] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	useEffect(() => () => cleanupRef.current(), []);
	const onPointerDown = (event) => {
		startYRef.current = event.clientY;
		setIsDragging(true);
		setDragOffset(0);
		const handleMove = (moveEvent) => {
			setDragOffset(Math.max(0, moveEvent.clientY - startYRef.current));
		};
		const handleUp = (upEvent) => {
			cleanupRef.current();
			setIsDragging(false);
			setDragOffset(0);
			if (upEvent.clientY - startYRef.current > SHEET_DRAG_DISMISS_THRESHOLD) closeRef.current?.click();
		};
		cleanupRef.current = () => {
			window.removeEventListener("pointermove", handleMove);
			window.removeEventListener("pointerup", handleUp);
			window.removeEventListener("pointercancel", handleUp);
			cleanupRef.current = () => {};
		};
		window.addEventListener("pointermove", handleMove);
		window.addEventListener("pointerup", handleUp);
		window.addEventListener("pointercancel", handleUp);
	};
	return {
		closeRef,
		sheetStyle: isDragging ? {
			translate: `0 ${dragOffset}px`,
			transition: "none"
		} : void 0,
		dragHandleProps: { onPointerDown }
	};
}
var renderCloseButton = (props) => /* @__PURE__ */ jsx(Button, {
	...props,
	variant: "secondary",
	size: "sm",
	"aria-label": "Close",
	className: "absolute top-6 right-6 h-11 w-11 rounded-full border-0 p-0 max-sm:hidden",
	children: /* @__PURE__ */ jsx(IconX, { size: 24 })
});
/**
* Modal dialog overlay with backdrop. Compound component with `Dialog.Root`,
* `Dialog.Trigger`, `Dialog.Title`, `Dialog.Description`, and `Dialog.Close`.
*
* @example
* ```tsx
* <Dialog.Root>
*   <Dialog.Trigger render={(p) => <Button aria-label="Delete" {...p}>Delete</Button>} />
*   <Dialog className="p-8">
*     <Dialog.Title>Delete Item</Dialog.Title>
*     <Dialog.Description>This action cannot be undone.</Dialog.Description>
*     <Dialog.Close render={(p) => <Button aria-label="Delete" {...p}>Delete</Button>} />
*   </Dialog>
* </Dialog.Root>
* ```
*
* @example Alert Dialog for destructive actions
* ```tsx
* <Dialog.Root role="alertdialog">
*   <Dialog.Trigger render={(p) => <Button aria-label="Delete Project" {...p}>Delete Project</Button>} />
*   <Dialog className="p-8">
*     <Dialog.Title>Delete Project?</Dialog.Title>
*     <Dialog.Description>This action cannot be undone.</Dialog.Description>
*     <Dialog.Close render={(p) => <Button aria-label="Cancel" variant="secondary" {...p}>Cancel</Button>} />
*     <Dialog.Close render={(p) => <Button aria-label="Delete" {...p}>Delete</Button>} />
*   </Dialog>
* </Dialog.Root>
* ```
*/
function DialogContent({ className, children, size = ATOMS_DIALOG_DEFAULT_VARIANTS.size, title, hideCloseButton = false, container: containerProp }) {
	const role = useDialogRole();
	const contextContainer = usePortalContainer();
	const container = containerProp ?? contextContainer ?? void 0;
	const BasePortal = role === "alertdialog" ? DialogPortal : DialogPortal;
	const BaseBackdrop = role === "alertdialog" ? DialogBackdrop : DialogBackdrop;
	const BasePopup = role === "alertdialog" ? DialogPopup : DialogPopup;
	const { closeRef, sheetStyle, dragHandleProps } = useSheetDragDismiss();
	const renderCloseTarget = useCallback((props) => /* @__PURE__ */ jsx(Button, {
		...props,
		ref: closeRef,
		tabIndex: -1,
		"aria-hidden": "true",
		"aria-label": "Close",
		className: "hidden"
	}), [closeRef]);
	return /* @__PURE__ */ jsxs(BasePortal, {
		container,
		children: [/* @__PURE__ */ jsx(BaseBackdrop, { className: "fixed inset-0 bg-od-bg-scrim/[0.72] transition-all duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0" }), /* @__PURE__ */ jsxs(BasePopup, {
			className: cn(dialogVariants({ size }), "p-8 sm:p-12", className),
			style: sheetStyle,
			children: [
				!hideCloseButton && /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(Row, {
						...dragHandleProps,
						className: "absolute -top-7 left-1/2 z-10 h-8 w-24 -translate-x-1/2 cursor-grab touch-none items-center justify-center active:cursor-grabbing sm:hidden",
						children: /* @__PURE__ */ jsx(Box, {
							"aria-hidden": "true",
							className: "h-2 w-22 rounded-full bg-od-bg-primary/72"
						})
					}),
					/* @__PURE__ */ jsx(DialogClose, { render: renderCloseTarget }),
					/* @__PURE__ */ jsx(DialogClose, {
						"aria-label": "Close",
						render: renderCloseButton
					})
				] }),
				title != null && /* @__PURE__ */ jsx(DialogTitle, {
					className: cn("pb-4 sm:pb-6", !hideCloseButton && "sm:pr-8"),
					children: title
				}),
				children
			]
		})]
	});
}
function DialogRoot(props) {
	if (props.role === "alertdialog") {
		const { role, children, ...rest } = props;
		return /* @__PURE__ */ jsx(DialogRoleContext.Provider, {
			value: role,
			children: /* @__PURE__ */ jsx(AlertDialogRoot, {
				...rest,
				children
			})
		});
	}
	const { role, children, ...rest } = props;
	return /* @__PURE__ */ jsx(DialogRoleContext.Provider, {
		value: role,
		children: /* @__PURE__ */ jsx(DialogRoot$1, {
			...rest,
			children
		})
	});
}
DialogRoot.displayName = "Dialog.Root";
function DialogTrigger({ children, ...props }) {
	return /* @__PURE__ */ jsx(useDialogRole() === "alertdialog" ? DialogTrigger$1 : DialogTrigger$1, {
		...props,
		children
	});
}
DialogTrigger.displayName = "Dialog.Trigger";
function DialogTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx(useDialogRole() === "alertdialog" ? DialogTitle$1 : DialogTitle$1, {
		className: cn("text-subheader-lg text-od-content-primary sm:text-header-lg", className),
		...props
	});
}
DialogTitle.displayName = "Dialog.Title";
function DialogDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx(useDialogRole() === "alertdialog" ? DialogDescription$1 : DialogDescription$1, {
		className,
		...props
	});
}
DialogDescription.displayName = "Dialog.Description";
function DialogClose({ children, ...props }) {
	return /* @__PURE__ */ jsx(useDialogRole() === "alertdialog" ? DialogClose$1 : DialogClose$1, {
		...props,
		children
	});
}
DialogClose.displayName = "Dialog.Close";
var Dialog = Object.assign(DialogContent, {
	Root: DialogRoot,
	Trigger: DialogTrigger,
	Title: DialogTitle,
	Description: DialogDescription,
	Close: DialogClose
});
//#endregion
export { ATOMS_DIALOG_DEFAULT_VARIANTS, ATOMS_DIALOG_STYLING, ATOMS_DIALOG_VARIANTS, Dialog, DialogClose, DialogDescription, DialogRoot, DialogTitle, DialogTrigger, dialogVariants };

